import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Close, InfoCircle, Warning } from "@/assets/icons/ds";
import { Link } from "@/components/Interface/Link/Link";

export type BannerVariant = "news" | "warning";

const variantClassMapping: Record<
  BannerVariant,
  { panel: string; badge: string }
> = {
  news: {
    panel: "bg-surface-quaternary",
    badge: "bg-surface-info",
  },
  warning: {
    panel: "bg-surface-accent-rose",
    badge: "bg-surface-error",
  },
};

const variantIcon: Record<BannerVariant, typeof InfoCircle> = {
  news: InfoCircle,
  warning: Warning,
};

export interface BannerProps {
  dataTestid?: string;
  className?: string;
  variant: BannerVariant;
  text: ReactNode;
  cta?: {
    label: string;
    link: string;
  };
  onDismiss?: () => void;
}

/**
 * Full-bleed banner (`dire-banner`): a coloured icon column, the message and an
 * optional CTA link, plus an optional dismiss button.
 */
export const Banner = ({
  dataTestid,
  className,
  variant,
  text,
  cta,
  onDismiss,
}: BannerProps) => {
  const { t } = useTranslation();
  const variantClasses = variantClassMapping[variant];
  const Icon = variantIcon[variant];

  return (
    <div
      data-testid={dataTestid}
      className={clsx(
        "flex w-full items-start gap-2 p-2 text-foreground-primary",
        variantClasses.panel,
        className,
      )}
    >
      <span
        className={clsx(
          "flex w-10 shrink-0 items-center justify-center self-stretch rounded-md text-foreground-primary-inverse",
          variantClasses.badge,
        )}
      >
        <Icon className="size-6" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1 p-2">
        <p className="text-body-sm leading-md tracking-[0.28px]">{text}</p>
        {cta && (
          <Link href={cta.link} size="sm" target="_blank" rel="noreferrer">
            {cta.label}
          </Link>
        )}
      </div>
      {onDismiss && (
        <button
          className="flex size-10 shrink-0 items-center justify-center rounded-md transition-transform hover:rotate-90"
          onClick={onDismiss}
          type="button"
          aria-label={t("common.buttons.close")}
        >
          <Close className="size-6" />
        </button>
      )}
    </div>
  );
};
